import { Injectable } from '@angular/core';
import { EventService } from '../../services/calendar/event.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EventActions, EventApiActions } from './event.actions';
import { catchError, concatMap, map, of, switchMap, tap } from 'rxjs';
import { CalendarFacadeService } from '../../services/calendar/calendar-facade.service';
import { ObjectUtilsService } from '../../services/core/object-utils.service';
import { AuthenticationFacade } from '../authentication/authentication.facade';
import { filterNullish } from '../../services/rxjs-utils';

@Injectable()
export class EventEffects {
    constructor(
        private eventService: EventService,
        private actions$: Actions,
        private calenderFacadeService: CalendarFacadeService,
        private objectUtilsService: ObjectUtilsService,
        private authenticationFacade: AuthenticationFacade
    ) {}

    loadEvents$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EventApiActions.loadEvents),
            switchMap(({ query }) =>
                this.eventService.loadEventsBetweenDates(query).pipe(
                    tap(),
                    switchMap((events) =>
                        this.calenderFacadeService.selectCoursesByEvents(events).pipe(
                            map(() =>
                                events.map((event) => {
                                    // const course: Course | undefined = courses.find((course) => course.id === event.course?.id);
                                    // event.course.courseCategory = course?.courseCategory;
                                    // event.course.trainingLevel = course?.courseDetail.trainingLevel;
                                    return event;
                                })
                            ),
                            map((events) => EventActions.loadEventsSuccess({ events })),
                            catchError((error) => of(EventActions.loadEventsFailure({ error })))
                        )
                    )
                )
            )
        );
    });

    saveEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EventApiActions.saveEvent),
            switchMap(({ event, updateSeries, isUpdate, sendEventUpdate }) => this.eventService.saveEvent(event, updateSeries, isUpdate, sendEventUpdate)),
            map((events) => {
                return EventActions.saveEventSuccess({ events });
            }),
            catchError((error) => of(EventActions.saveEventFailure({ error })))
        );
    });

    loadEventById$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EventApiActions.loadEventById),
            switchMap(({ id }) =>
                this.eventService.loadEventById(id).pipe(
                    map((event) => {
                        event.instructor = this.objectUtilsService.buildName(event.instructor);
                        event.participants = event.participants.map((participant) => {
                            participant.userAccount = this.objectUtilsService.buildName(participant.userAccount);
                            return participant;
                        });
                        return event;
                    })
                )
            ),
            map((event) => EventActions.loadEventByIdSuccess({ event })),
            catchError((error) => of(EventActions.loadEventsFailure({ error })))
        );
    });

    deleteEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EventApiActions.deleteEvent),
            switchMap(({ event, deleteSeries }) =>
                this.eventService.deleteEvent(event.id!, deleteSeries).pipe(
                    map(() => EventActions.deleteEventSuccess({ event: event, deleteSeries: deleteSeries })),
                    catchError((error) => of(EventActions.deleteEventFailure({ error })))
                )
            )
        );
    });

    cancelEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EventApiActions.cancelEvent),
            switchMap((action) => this.eventService.cancelEvent(action.event)),
            map((event) => EventActions.cancelEventSuccess({ event })),
            catchError((error) => of(EventActions.cancelEventFailure({ error })))
        );
    });

    eventTakePlace$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EventApiActions.eventTakePlace),
            switchMap((action) => this.eventService.eventTakePlace(action.event)),
            map((event) => EventActions.eventTakePlaceSuccess({ event })),
            catchError((error) => of(EventActions.eventTakePlaceFailure({ error })))
        );
    });

    moveEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EventApiActions.moveEvent),
            concatMap((action) => this.eventService.moveEvent(action.event)),
            map((event) => EventActions.moveEventSuccess({ event })),
            catchError((error) => of(EventActions.loadEventsFailure({ error })))
        );
    });

    subscribeToEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EventApiActions.subscribeToEvent),
            switchMap((action) => {
                return this.authenticationFacade.user$.pipe(
                    filterNullish(),
                    switchMap((user) => this.eventService.subscribeToEvent(user.id!, action.eventId)),
                    map((event) => EventActions.subscribeToEventSuccess({ event })),
                    catchError((error) => of(EventActions.subscribeToEventFailure({ error })))
                );
            })
        );
    });

    unsubscribeFromEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EventApiActions.unsubscribeFromEvent),
            switchMap((action) => {
                return this.authenticationFacade.user$.pipe(
                    filterNullish(),
                    switchMap((user) => this.eventService.unsubscribeFromEvent(user.id!, action.eventId)),
                    map((event) => EventActions.unsubscribeFromEventSuccess({ event })),
                    catchError((error) => of(EventActions.unsubscribeFromEventFailure({ error })))
                );
            })
        );
    });

    subscribeToWorkshop$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EventApiActions.subscribeToWorkshop),
            switchMap((action) => {
                return this.authenticationFacade.user$.pipe(
                    filterNullish(),
                    switchMap((user) => this.eventService.subscribeToWorkshop(user.id!, action.courseId)),
                    map((events) => EventActions.subscribeToWorkshopSuccess({ events })),
                    catchError((error) => of(EventActions.subscribeToWorkshopFailure({ error })))
                );
            })
        );
    });

    unsubscribeFromAllWorkshopEvents$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EventApiActions.unsubscribeFromAllWorkshopEvents),
            switchMap((action) => {
                return this.authenticationFacade.user$.pipe(
                    filterNullish(),
                    switchMap((user) => this.eventService.unsubscribeFromAllWorkshopEvents(user.id!, action.courseId)),
                    map((events) => EventActions.unsubscribeFromAllWorkshopEventsSuccess({ events })),
                    catchError((error) => of(EventActions.unsubscribeFromAllWorkshopEventsFailure({ error })))
                );
            })
        );
    });
}
